/* eslint-disable no-console */
import SPLM from '@atlas/api-javascript-sdk/no/splm';
import '@atlas/api-javascript-sdk/no/Object';
import '@atlas/api-javascript-sdk/no/TransientObject';
import '@atlas/api-javascript-sdk/no/TaggedObjectCollection';

// AtlasId is a tuple of an array of strings and a string for TaggedObject but could be number for transient object
/** @typedef {[string[], string]} AtlasId */
/** @typedef {{ObjectId:AtlasId, Class:string}} SPLMObject */
/** @typedef {SPLMObject & Record<string, any>} AtlasObject */
/** @typedef {typeof SPLM.Object} SPLMObjectCls */

// // Super-nasty hack for the atlas-tester environment (and perhaps any
// // other that uses webpack). If there is a global SPLM namespace available,
// // use that in preference to the one that this script has imported.
// const SPLM = window.SPLM || _SPLM;

/**
 * @summary Enables the SPLM.api mechanism
 * @description Function to ensure that the SPLM api request queuing
 * mechanism (SPLM.api) has been enabled. Asking for properties and
 * calling methods on SDK objects will push requests into this queue.
 * @returns {Array} Previous state of SPLM.api
 * @ignore
 */
function enableApiQueue() {
    if (SPLM.api instanceof Array) {
        return SPLM.api;
    }
    SPLM.api = [];
    return undefined;
}

/**
 * @summary Disables the SPLM.api mechanism
 * @description Function to ensure that the SPLM api request queuing
 * mechanism (SPLM.api) is not active. Asking for and setting properties
 * will just affect the in-client SDK object and will not generate
 * requests to send to the Atlas server.
 * @returns {Array} Previous state of SPLM.api
 * @ignore
 */
function disableApiQueue() {
    const oldApiQueue = SPLM.api;
    delete SPLM.api;
    return oldApiQueue;
}

/**
 * @summary Resets the SPLM.api queue back to its previous state
 * @description Function to return the SPLM api request queuing
 * mechanism back to its state before enableApiQueue or disableApiQueue
 * was called.
 * @param {Array} originalQueueState - the value returned from enableApiQueue or disableApiQueue
 * @ignore
 */
function restoreApiQueue(originalQueueState) {
    if (originalQueueState) {
        SPLM.api = originalQueueState;
    } else {
        console.assert(SPLM.api === undefined || SPLM.api.length === 0, 'restoreApiQueue: SPLM.api queue is not empty (discarded Atlas requests)');
        delete SPLM.api;
    }
}

/**
 * @summary Runs a function with the SPLM.api mechanism enabled.
 * @description Function wrapper which ensures that the SPLM api
 * request queuing mechanism has been enabled while a function is
 * being executed. Ensures that the SPLM api queue is reset once
 * the function has finished.
 * @param {Function} functionToCall - function to be called
 * @returns {any} the value returned from calling 'functionToCall'
 */
export function withApiQueueEnabled(functionToCall) {
    let result = undefined;
    const oldApiQueueState = enableApiQueue();
    try {
        result = functionToCall();
    } finally {
        restoreApiQueue(oldApiQueueState);
    }
    return result;
}

/**
 * @summary Runs a function with the SPLM.api mechanism disabled.
 * @description Function wrapper which ensures that the SPLM api
 * request queuing mechanism has been disabled while a function is
 * being executed. Ensures that the SPLM api queue is reset once
 * the function has finished.
 * @param {Function} functionToCall - function to be called
 * @returns {any} the value returned from calling 'functionToCall'
 */
export function withApiQueueDisabled(functionToCall) {
    let result = undefined;
    const oldApiQueueState = disableApiQueue();
    try {
        result = functionToCall();
    } finally {
        restoreApiQueue(oldApiQueueState);
    }
    return result;
}

/**
 * @summary Tests whether 'className' corresponds to an Atlas SDK class name
 * @param {string} className - the name to check
 * @returns {boolean}
 */
export function isSdkClassName(className) {
    return className.startsWith('SPLM.');
}

/**
 * @summary Tests whether 'obj' is an instance of one of the Atlas SDK classes.
 * @description
 * Determines whether or not 'obj' is an instance of one of the Atlas SDK classes
 * (and so has properties and methods that can be accessed to issue requests to
 * the Atlas server) or is just a plain old Javascript object.
 *
 * NOTE:
 * This function does not use the SDK classes defined in the SPLM namespace
 * to determine if this object is an instance of one of those classes. This
 * is for two reasons
 *   1. There is no common superclass for the SDK classes to inherit from.
 *      Anything trying to use instanceof to determine if the object is
 *      constructed from one of those classes would have to test the object
 *      against multiple root classes
 *   2. There can be *multiple* definitions of the same SDK classes as each
 *      time the source script is read, it will generate a new class. Some
 *      source bundling techniques may include some of these scripts inside
 *      a bundle, and when this happens this can result in extra copies of
 *      these classes being defined.
 * @param {object} obj - an object
 * @returns {boolean} true if obj is an instance of an Atlas SDK class
 */
export function isSdkObject(obj) {
    // This is at best a *guess* that this object is an SDK object. We check that it
    // is an object (but not a normal JS object) and that it has a class defined for it.
    return obj instanceof Object && obj.constructor !== Object && obj._d !== undefined && obj._d.Class !== undefined && isSdkClassName(obj._d.Class);
}

/**
 * @summary Returns the property descriptor for the named property of the object.
 * @description Retrieves the property descriptor for a named, inherited property.
 * This is the opposite of the Object.getOwnPropertyDescriptor function which only
 * returns properties that exist on the object itself. This function ignores the
 * "own" properties and only identifies inherited properties.
 * @param {object} obj - an object
 * @param {string} propName - the name of an inherited property of the object
 * @returns {object} the property descriptor (or undefined if no such inherited
 * property exists)
 */
function getInheritedPropertyDescriptor(obj, propName) {
    const proto = Object.getPrototypeOf(obj);
    if (proto === null) {
        return {}; // Empty property descriptor for non-existent property name.
    } else if (proto.hasOwnProperty(propName)) {
        return Object.getOwnPropertyDescriptor(proto, propName);
    }
    return getInheritedPropertyDescriptor(proto, propName);
}

/**
 * @summary Tests if 'propName' corresponds to an Atlas SDK property for 'obj'
 * @param {object} obj - an object from the Atlas SDK
 * @param {string} propName - the name of a property
 * @returns {boolean} true if propName is the name of a property on the SDK object.
 *
 * NOTE:
 *  1. Ignores any object which isn't from the SPLM hierarchy
 *  2. Ignores all properties of the object itself (it only looks for
 *     properties defined in the SPLM class that the object inherits from)
 *  3. Carefully avoids asking the object for the property value as this
 *     will mutate the object for some property values
 *  4. We can't indentify read-only properties of this object as every
 *     property is assigned a mutator (so they all have a 'set' function)
 */
export function isSdkPropertyName(obj, propName) {
    console.assert(propName !== undefined, 'isSdkPropertyName takes two arguments - the SDK object and the property name');
    return isSdkObject(obj) && propName[0] !== '_' && propName in obj && getInheritedPropertyDescriptor(obj, propName).get !== undefined;
}

/**
 * @summary Tests if 'propName' is the name of an Atlas SDK method for 'obj'
 * @param {object} obj - an object from the Atlas SDK
 * @param {string} propName - the name of a property
 * @returns {boolean} true if propName is the name of a function to call on the SDK object.
 */
export function isSdkFunctionName(obj, propName) {
    console.assert(propName !== undefined, 'isSdkFunctionName takes two arguments - the SDK object and the property name');
    return isSdkObject(obj) && propName[0] !== '_' && propName in obj && getInheritedPropertyDescriptor(obj, propName).get === undefined;
}

/**
 * @summary Returns sdk class from sdk class name.
 * @param {string} sdkClassName - the name of a class from the Atlas SDK (e.g. "SPLM.Face")
 * @returns {SPLMObjectCls|undefined} - SDK class if found else returns undefined.
 */
export function getSdkClass(sdkClassName) {
    // Deal with SPLM.NXColor objects which the Atlas worker returns with a Class name of "NXOpen.NXColor"
    if (sdkClassName.startsWith('NXOpen.')) {
        sdkClassName = sdkClassName.replace('NXOpen.', 'SPLM.');
    }
    if (sdkClassName.startsWith('SPLM.')) {
        return sdkClassName
            .substring(5)
            .split('.')
            .reduce((obj, name) => (obj !== undefined ? obj[name] : undefined), SPLM);
    }
    return undefined;
}

/**
 * @summary Constructs an empty Atlas SDK object from the name of a class
 * @param {string} sdkClassName - the name of a class from the Atlas SDK (e.g. "SPLM.Face")
 * @returns {AtlasObject} an empty instance of that class
 */
export function createSdkObjectOfClass(sdkClassName) {
    let classConstructor = getSdkClass(sdkClassName);
    if (classConstructor !== undefined) {
        return new classConstructor();
    }
    // based on previous logic if incoming class is sdk class but we couldn't
    // find class constructor then use SPLM.Object.
    else if (isSdkClassName(sdkClassName)) {
        console.warn('Missing import for SDK class', sdkClassName);
        return new SPLM.Object();
    }

    throw new Error('Cannot create SDK object with class name ' + sdkClassName);
}

/**
 * @summary Converts plain-old JS objects into Atlas SDK objects.
 * @param {object} json - an object (typically a response to an Atlas request)
 * @returns {object} the equivalent object but with all objects from the Atlas SDK replaced with instances of the corresponding class
 */
export function jsonToSdk(json) {
    if (json instanceof Object && !isSdkObject(json)) {
        if (json.Class !== undefined) {
            const obj = createSdkObjectOfClass(json.Class);
            for (const propName in json) {
                const jsonPropValue = json[propName];
                obj._d[propName] = jsonToSdk(jsonPropValue);
            }
            return obj;
        }
        const obj = new json.constructor();
        for (const propName in json) {
            const jsonPropValue = json[propName];
            obj[propName] = jsonToSdk(jsonPropValue);
        }
        return obj;
    }
    return json;
}

/**
 * @summary Reads a property value from an object without issuing any Atlas requests.
 * @description Function to simply read a property value from an SDK object.
 * It performs the equivalent of sdkObject[propName] for any other JS object.
 * It avoids the object from self-modifying, and it ignores/discards the Atlas
 * request object that the getter method produces.
 *
 * The purpose of this function is to get any property value that the client
 * already knows about, and to avoid issuing any requests for properties that
 * are not yet known.
 * @param {object} sdkObject - any object. Assumed to be a SDK object if it has a "_d" property.
 * @param {string} propName - the name of the property to be retrieved
 * @returns {any} the value of the property (or undefined)
 */
export function getSdkObjectProperty(sdkObject, propName) {
    // Trivial case - handed some object which isn't a true SDK object
    // (perhaps a JSON object which looks a bit like one, or perhaps just
    // any other JS object). In this case just return the property value
    // that the object holds.
    if (!isSdkObject(sdkObject)) {
        if (sdkObject instanceof Object) {
            return sdkObject[propName];
        }
        return undefined;
    }

    // If we can, read the property value directly from the internal
    // data of the SDK object. This avoids the problem where the object
    // mutates itself and discards the property to be read
    let propVal = sdkObject._d[propName];

    // If the property already exists on the SDK object, but has been
    // assigned a value of {}, this indicates a request for that property
    // value has been made. In this case we want to treat this as
    // "no value" rather than {}, but we don't want to make any changes
    // to this object itself either.
    if (isEmptyObject(propVal)) {
        return undefined;
    }

    // If we have no property, try reading it directly from the object. We
    // do this to ensure that if the property is a sub-object, this will be
    // generated as if it always existed. These properties are always
    // read-only and so it is better to return the value that it would
    // have if asked for directly, rather than giving the impression that
    // the sub-object doesn't exist.
    if (propVal === undefined) {
        // Requesting a property from a SDK object may queue up requests
        // in the SPLM.api array (if it exists). Ensure that we don't
        // generate any of these.
        const splmApiLength = SPLM.api instanceof Array ? SPLM.api.length : -1;

        // Read the property from the SDK object. This may auto-construct
        // a sub-object for this property name, or it may just return a
        // request object for the property value. Or it may just return
        // "undefined" if "propName" isn't a SDK property name.
        propVal = sdkObject[propName];

        // Discard any API request that this may have queued up
        if (splmApiLength !== -1) SPLM.api.length = splmApiLength;

        // If we have modified sdkObject._d by requesting this property then
        // indicate that the property didn't exist and undo the change to
        // "sdkObject".
        //
        // NOTE: We don't have to worry about the case of sub-objects. These
        // are no-longer stored in the "_d" structure, so if we find anything
        // in that object, this must be a normal property request and we can
        // get rid of it as we already know that the value wasn't defined before.
        //
        // NOTE: OK, so start worrying about it again. We have *NO* *CONTROL*
        // over which version of the Atlas SDK we might be using, and so have
        // *NO* *IDEA* over which of its many and varied behaviors which might
        // have to contend with, so *ASSUME* *THE* *WORST* at all times and
        // patch *EVERYTHING* up.
        if (propName in sdkObject._d) {
            if (isEmptyObject(sdkObject._d[propName])) {
                delete sdkObject._d[propName];
                propVal = undefined;
            }
        }
    }

    return propVal;
}

/**
 *
 * @param obj
 * @returns {boolean}
 */
function isErrorObject(obj) {
    return obj instanceof Object && 'API_Error' in obj;
}

/**
 * Use this class to throw error return from atlas request.
 */
export class AtlasError extends Error {
    constructor(errorMessage, errorCode) {
        super(errorMessage);
        this.errorCode = errorCode;
    }
}

/**
 * @param {object} obj - a result object (or part of one) returned from the Atlas server
 * @description throws a JS error if the object contains error information returned from
 * the server.
 */
export function throwIfApiError(obj) {
    if (isErrorObject(obj)) {
        throw new AtlasError(obj.API_Message, obj.API_Error);
    } else if (obj instanceof Object) {
        for (const x of Object.values(obj)) {
            if (x instanceof Object) {
                throwIfApiError(x);
            }
        }
    }
}

/**
 * @summary Checks if the Atlas SDK class represented by `derivedClassName` is a subclass
 * of the class represented by `baseClassName`.
 * @description This function gets the class actual class represented by the two parameters
 * and checks whether `derivedClassName` object is an instance of the `baseClassName` class.
 * **NOTE** Client must ensure that the class names has already imported from Atlas SDK.
 * @param {string} baseClassName - The name of the base class from the Atlas SDK to check
 *                                 against
 * @param {string} derivedClassName - The name of the derived class from the Atlas SDK
 * @returns {boolean} Indicates if `derivedClassName` is a subclass of `baseClassName`
 * @example
 * const case1 = isDerivedClass('SPLM.Curve', 'SPLM.Arc');
 * const case2 = isDerivedClass('SPLM.Line', 'SPLM.Arc');
 * console.log(case1, case2); // 'true', 'false'
 */
export function isDerivedClass(baseClassName, derivedClassName) {
    if (!isSdkClassName(baseClassName) || !isSdkClassName(derivedClassName)) {
        return false;
    }
    if (baseClassName === derivedClassName) {
        return true;
    }
    const derivedClass = getSdkClass(derivedClassName);
    const baseClass = getSdkClass(baseClassName);
    if (!derivedClass || !baseClass) return false;

    let prototype = Object.getPrototypeOf(derivedClass);
    while (prototype) {
        if (prototype === baseClass) {
            return true;
        }
        prototype = Object.getPrototypeOf(prototype);
    }
    return false;
}

/**
 * @summary Updates "sdkObject" with the data held in "jsonData".
 * @param {object} sdkObject - an instance of one of the SDK classes which is to be updated
 * @param {object} jsonData = an Object (typically a response to an Atlas request) containing data to be merged into "sdkObject"
 * @returns {object} the updated "sdkObject"
 */
export function mergeObjects(sdkObject, jsonData) {
    return withApiQueueDisabled(() => _mergeObjects(sdkObject, jsonData));
}

/**
 *
 * @param sdkObject
 * @param jsonData
 */
function _mergeObjects(sdkObject, jsonData) {
    if (sdkObject instanceof Object && jsonData instanceof Object) {
        // Simple paranoia. If we can tell what objects the sdkObject and jsonData represent,
        // check to ensure that they are the same object (or at least the same type of object).
        // Note that the ObjectId may not always be a simple number (the tag of the object in
        // the Atlas session) and we do want the merge operation to mutate this property from
        // being a logical definion of the object (e.g. the part that is the Work part associated
        // with a PartCollection) into a simple number that actually specifies it directly.
        if (sdkObject.Class !== undefined && jsonData.Class !== undefined) {
            if (sdkObject.Class !== jsonData.Class) {
                if (!isDerivedClass(sdkObject.Class, jsonData.Class)) {
                    console.assert(
                        sdkObject.Class === jsonData.Class,
                        'mergeObjects: asked to merge two different classes of SDK object',
                        sdkObject,
                        jsonData
                    );
                    return;
                }
            }
        }
        if (typeof sdkObject.ObjectId === 'number' && typeof jsonData.ObjectId === 'number') {
            console.assert(
                sdkObject.ObjectId === jsonData.ObjectId,
                'merge_objects: asked to merge together two different objects',
                sdkObject,
                jsonData
            );
            if (sdkObject.ObjectId !== jsonData.ObjectId) {
                return;
            }
        } else if (sdkObject.ObjectId instanceof Array && jsonData.ObjectId instanceof Array) {
            console.assert(
                JSON.stringify(sdkObject.ObjectId) === JSON.stringify(jsonData.ObjectId),
                'merge_objects: asked to merge together two different objects',
                sdkObject,
                jsonData
            );
            if (JSON.stringify(sdkObject.ObjectId) !== JSON.stringify(jsonData.ObjectId)) {
                return;
            }
        }

        // To merge these objects together, we get the plain-old JS object
        // that is the "_d" property of the SDK object. This allows us to
        // easily merge the contents of the JSON data into it without having
        // to worry about the accessor/mutator behaviors of the SDK class.
        // Note that not all SDK classes include a "_d" attribute (e.g.
        // SPLM.Point3d) so we only use it if it exists.
        sdkObject = sdkObject._d || sdkObject;

        // Treat jsonData similarly in case something has already converted it
        // from the simple JSON structure that we expect to be processing.
        console.assert(jsonData._d === undefined, 'mergeObjects given jsonData which is not plain-old-JSON', jsonData);
        jsonData = jsonData._d || jsonData;

        for (let [propName, propValue] of Object.entries(jsonData)) {
            // If this property corresponds to some kind of error report from the
            // Atlas server, don't try to merge it into the sdkObject.
            // Skip the warning messages too.
            if (!isErrorObject(propValue) && !propName.startsWith('API_Warning')) {
                let oldValue = sdkObject[propName];

                // // The SDK mechanism for requesting property values is to write an empty
                // // object onto the object as the property to be requested. If we see that
                // // sdkObject has such a property value, we need to treat it as if it didn't
                // // exist (and we remove the value as well)
                // if (isEmptyObject(oldValue)) {
                //     delete sdkObject[propName];
                //     oldValue = undefined;
                // }

                // We can get an empty object returned from the Atlas server in the JSON
                // structure which represents nothing/no-such-object. If we see this, we
                // treat it as "null".
                if (isEmptyObject(propValue)) {
                    propValue = null;
                }

                // If the new value is an Array and the old property value is not
                // an array (this sort of transformation in property types can
                // happen for the ObjectId property where a request may
                // pass in a JSON object describing how to construct an object, and
                // the reply may be an array containing the Atlas id values), then
                // we want to ignore/discard the old value and replace it with
                // an empty array into which we merge the values from the new one.
                if (propValue instanceof Array && !(oldValue instanceof Array)) {
                    oldValue = [];
                    //console.debug( 'mergeObjects: adding Array sub-property', propName );
                    sdkObject[propName] = oldValue;
                }

                // Similarly if the new value is an Object but the old value is not
                // (the most common case is when oldValue is undefined), we discard
                // the old value and replace it with an empty object.
                if (propValue instanceof Object && !(oldValue instanceof Object)) {
                    if (propValue.Class !== undefined && isSdkClassName(propValue.Class)) {
                        oldValue = createSdkObjectOfClass(propValue.Class);
                    } else {
                        oldValue = {};
                    }
                    //console.debug( 'mergeObjects: adding Object sub-property', propName );
                    sdkObject[propName] = oldValue;
                }

                if (oldValue instanceof Object && propValue instanceof Object) {
                    _mergeObjects(oldValue, propValue);
                } else if (propValue !== undefined) {
                    //console.debug( 'mergeObjects: adding property', propName );
                    sdkObject[propName] = propValue;
                } else if (oldValue !== undefined) {
                    // Keep values on sdkObject even if they weren't found in the
                    // JSON object being merged in. There is no reason why we would
                    // always have requested all of the properties of the original
                    // object.
                }
            } else {
                //console.debug( 'mergeObjects: skipping', propName, 'with error value', propValue );
            }
        }
    }
}

/**
 * @summary Tests a value to see if it is the empty object
 * @description
 * The Atlas SDK makes extensive use of empty objects to represent
 * a kind of undefined value. For example, when reading a property
 * value, the JSON contains an object which contains the named
 * property but with a value which is the empty object. The server
 * understands that this is asking for the value rather than trying
 * to assign a value for this property. When examining Atlas requests
 * (and replies), it is important to be able to identify this usage
 * in order to correctly understand what is meant.
 *
 * In Javascript there is no easy way to perform an equality test
 * on objects, so obj === {} doesn't work. The === (and ==) operators
 * only indicate if they are the same object (and not two objects
 * that have the same properties). Hence the need for this function.
 *
 * We don't count Arrays as empty objects (although an Array is
 * an Object and therefore an empty Array is a kind of enpty Object)
 * because we are trying to test obj == {} and if obj is [] then
 * because [] !== {} this function should return false.
 *
 * The Atlas SDK now generates non-empty objects that the Atlas server
 * will *treat* as empty. When getting a property value, the request
 * passes in an object containing information describing how to
 * assign a local value for the result. This object has this form
 *    { '%PropName': '$var64' }
 * i.e. there is a property of this object with a name starting with '%'.
 * Treat these as empty objects too.
 * @param {any} obj - any value
 * @returns {boolean} true if 'obj' is an empty object
 */
export function isEmptyObject(obj) {
    for (const x in obj) {
        if (x[0] !== '%') {
            return false;
        }
    }
    return obj instanceof Object && !(obj instanceof Array);
}

// Identify all of the SDK properties for this object. These are the ones
// which are associated with a "get" method in the SPLM class definition.
const unwantedPropertyNames = new Set(['ObjectId', 'Class', 'LocalVariable', 'GlobalVariable', 'UserObjectId', 'GlobaVariable' /* WTF? */]);
/**
 *
 * @param sdkObject
 * @returns {string[]}
 */
function getAllPropertyNames(sdkObject) {
    const propNames = [];

    // Get all of the property names defined by the class
    // (prototype) of this object, including all those
    // inherited from superclasses
    let object = sdkObject;
    let proto = Object.getPrototypeOf(object);

    while (proto !== null) {
        object = proto;
        proto = Object.getPrototypeOf(object);

        Object.getOwnPropertyNames(object).forEach((name) => propNames.push(name));
    }

    // Filter the list of property names to just those which
    // correspond to SDK properties (i.e. have an associated
    // "get" method)
    return propNames.filter((name) => !unwantedPropertyNames.has(name)).filter((name) => isSdkPropertyName(sdkObject, name));
}

/**
 * @summary Updates "sdkObject" with requests to retrieve all of the properties for that object.
 * @param {object} sdkObject - an instance of one of the Atlas SDK classes
 * @param {boolean} includingSubobjects - a flag indicating whether or not to add requests for all properties of sub-objects of "sdkObject"
 * @returns {boolean} true if some properties were marked to be fetched from the server
 */
export function requestAllProperties(sdkObject, includingSubobjects = true) {
    let requestedSomeProperties = false;

    const propNames = getAllPropertyNames(sdkObject);
    console.debug('requestAppProperties', sdkObject, 'has properties', propNames);

    // For each property that isn't already a known value, update the "sdkObject"
    // to contain a request for the property.
    for (const key of propNames) {
        const propVal = getSdkObjectProperty(sdkObject, key);

        if (typeof propVal === 'object' && includingSubobjects) {
            if (requestAllProperties(propVal, includingSubobjects)) {
                requestedSomeProperties = true;
            }
        } else if (typeof propVal === 'undefined') {
            sdkObject[key];
            requestedSomeProperties = true;
        }
    }

    return requestedSomeProperties;
}

/**
 *
 * @param sdkObject
 * @param path
 * @returns {object}
 */
function identifySubObjectByPath(sdkObject, path) {
    while (sdkObject !== null && path.length > 0) {
        const propName = path.shift();
        if (isSdkPropertyName(sdkObject, propName)) {
            sdkObject = getSdkObjectProperty(sdkObject, propName);
            if (!(sdkObject instanceof Object)) {
                sdkObject = null;
            } else if (sdkObject._d && sdkObject._d.ObjectId) {
                // If SPLM.api is not defined (which should be the case for
                // this function) we don't want the sub-objects to have their
                // own ObjectId values. These will not be sent to the Atlas
                // server as independent requests, but as the sub-object of
                // the main object. The identification of this object is implicit
                // in the way that the JSON request is structured.
                delete sdkObject._d.ObjectId;
            }
        }
    }

    return sdkObject;
}

/**
 * @summary Updates "sdkObject" with requests to retrieve named properties for that object.
 * @description
 * The list of properties names to retrieve can include properties from sub-objects by
 * using a dot-notation (e.g. "Distance.RightHandSide" to get the "RightHandSide"
 * property of the SPLM.Expression that is referenced as "Distance")
 *
 * Any property names which are not Atlas SDK properties will be ignored.
 * @param {object} sdkObject - an instance of one of the Atlas SDK classes
 * @param {Array<string>} propertyNames - list of property names
 * @returns {boolean} true if some properties were marked to be fetched from the server
 */
export function requestProperties(sdkObject, propertyNames) {
    return withApiQueueDisabled(() => {
        //console.debug('requestProperties:', sdkObject, propertyNames);

        let requestedSomeProperties = false;

        for (const key of propertyNames) {
            const pathElements = key.split('.');
            const propName = pathElements.pop();
            const subObj = identifySubObjectByPath(sdkObject, pathElements);
            //console.debug('* identified', subObj, 'for', key);
            if (subObj !== null) {
                if (isSdkPropertyName(subObj, propName)) {
                    const propVal = getSdkObjectProperty(subObj, propName);
                    if (propVal === undefined) {
                        //console.debug('* requesting property', propName, 'from', subObj);

                        // Simply asking for a property will update the SDK object to fetch the property
                        // Some properties we cannot assign to (no "set" function supplied in the SDK)
                        // so the old mechanism of assigning a value of "{}" to ask for a property will
                        // not work any more.
                        subObj[propName];

                        requestedSomeProperties = true;
                    }
                }
            }
        }

        return requestedSomeProperties;
    });
}
